import axios from 'axios';
import { API_BASE_URL } from '../../config/api';

export type PaymentProvider = 'cashfree' | 'razorpay' | 'hdfc';

export const PAYMENT_PROVIDERS: { value: PaymentProvider; label: string }[] = [
  { value: 'cashfree', label: 'Cashfree' },
  { value: 'razorpay', label: 'Razorpay' },
  { value: 'hdfc', label: 'HDFC SmartGateway' },
];

/** Providers whose mini-app supports gateway-side recurring plans. */
export const RECURRING_PROVIDERS: PaymentProvider[] = ['cashfree', 'razorpay'];

const GATEWAY_ROOT = (
  import.meta.env.VITE_PAYMENT_GATEWAY_BASE_URL || 'https://cs.fabbuilder.com/cs-app'
).replace(/\/+$/, '');

const gatewayBase = (provider: string) => `${GATEWAY_ROOT}/${provider || 'cashfree'}`;

/** A plan the visitor can choose, with its plan id on each provider that offers it. */
export const PLAN_CADENCES = [
  'Daily', 'Weekly', 'Monthly', 'Quarterly', 'Half-Yearly', 'Yearly',
] as const;
/** One-time plans can also be 'Lifetime'. */
export const ONE_TIME_CADENCES = [...PLAN_CADENCES, 'Lifetime'] as const;
export type PlanCadence = (typeof ONE_TIME_CADENCES)[number];

export interface PaymentPlanOption {
  id: string;
  label: string;
  description?: string;
  /** Id of the plan in the API it was imported from, so a re-import updates it. */
  sourceId?: string;
  /** Plans with a price are defined here and created on the gateways when the form is saved. */
  price?: number | string;
  /** 'one_time' plans are charged once; the cadence is how long they last. Default recurring. */
  payment?: 'recurring' | 'one_time';
  cadence?: PlanCadence;
  /** "Every N" cadence units; only Daily/Weekly/Monthly/Yearly honour it. */
  intervalCount?: number | string;
  maxCycles?: number | string;
  discountMode?: 'none' | 'percentage' | 'flat';
  discountValue?: number | string;
  discountType?: 'first_time' | 'all_time';
  gstMode?: 'none' | 'included' | 'excluded';
  isPopular?: boolean;
  /** Existing gateway plans found on this tenant while filling from an API; the server verifies and adopts them on save. */
  adoptGatewayPlans?: Partial<Record<PaymentProvider, string>>;
  /** Gateway plan ids; filled in by the server for plans with a price. */
  gatewayPlans: Partial<Record<PaymentProvider, string>>;
  /** Server-owned: which plan config each gateway plan was created from. */
  gatewaySync?: Partial<Record<PaymentProvider, string>>;
}

export interface KeyValueRow {
  key: string;
  value: string;
}

export interface CreateFormOrderPayload {
  fieldName: string;
  provider: PaymentProvider;
  /** Id of the chosen plan option; plan mode only. */
  planId?: string;
  crmTenantId?: string;
  customer: { leadId: string; name?: string; email?: string; contact?: string };
  returnUrl: string;
  /** The page's query params, available to `{query.x}` tokens in order fields. */
  query?: Record<string, string>;
}

export interface GatewayPlan {
  id: string;
  name: string;
  /** Amount charged each cycle. */
  amount?: number;
  currency?: string;
  /** Cadence unit as the gateway reports it, e.g. MONTH, WEEK. */
  interval?: string;
  /** Number of units per cycle (every N months). */
  intervals?: number;
  maxCycles?: number;
  maxAmount?: number;
  type?: string;
  description?: string;
  raw: any;
}

const planRows = (response: any): any[] => {
  const candidates = [
    response,
    response?.data,
    response?.plans,
    response?.data?.plans,
    response?.data?.data,
    response?.items,
    response?.data?.items,
  ];
  return candidates.find(Array.isArray) || [];
};

const num = (value: unknown): number | undefined => {
  const n = Number(value);
  return value !== undefined && value !== null && value !== '' && Number.isFinite(n)
    ? n
    : undefined;
};

const normalizePlan = (raw: any): GatewayPlan => ({
  id: String(raw?.plan_id ?? raw?.planId ?? raw?.id ?? ''),
  name: String(
    raw?.plan_name ?? raw?.planName ?? raw?.name ?? raw?.item?.name ?? raw?.plan_id ?? raw?.id ?? '',
  ),
  amount: num(
    raw?.plan_recurring_amount ?? raw?.planRecurringAmount ?? raw?.item?.amount,
  ),
  currency: raw?.plan_currency ?? raw?.planCurrency ?? raw?.currency ?? raw?.item?.currency,
  interval: raw?.plan_interval_type ?? raw?.planIntervalType ?? raw?.period,
  intervals: num(raw?.plan_intervals ?? raw?.planIntervals ?? raw?.interval),
  maxCycles: num(raw?.plan_max_cycles ?? raw?.planMaxCycles),
  maxAmount: num(raw?.plan_max_amount ?? raw?.planMaxAmount),
  type: raw?.plan_type ?? raw?.planType,
  description: raw?.plan_note ?? raw?.description ?? raw?.item?.description,
  raw,
});

const UNIT: Record<string, string> = {
  day: 'Day', daily: 'Day', week: 'Week', weekly: 'Week',
  month: 'Month', monthly: 'Month', year: 'Year', yearly: 'Year',
};

/** "Month", "Every 3 Months", or the raw cadence if unrecognised. */
export const formatPlanCadence = (plan: GatewayPlan): string => {
  const raw = String(plan.interval || '');
  const unit = UNIT[raw.toLowerCase()];
  if (!unit) return raw;
  const count = plan.intervals && plan.intervals > 0 ? plan.intervals : 1;
  return count > 1 ? `${count} ${unit}s` : unit;
};

/** Cadence text for a plan defined in the form, e.g. "Month" or "Every 3 Months". */
export const isOneTimePlan = (plan?: PaymentPlanOption): boolean => plan?.payment === 'one_time';

/** "/ Month" for a recurring plan, "for 3 Months" or "one-time" for a one-time plan. */
export const formatOptionPriceSuffix = (plan: PaymentPlanOption): string => {
  if (!isOneTimePlan(plan)) return `/ ${formatOptionCadence(plan)}`;
  if (plan.cadence === 'Lifetime') return 'one-time';
  const cadence = formatOptionCadence(plan);
  const every = cadence.match(/^Every (\d+) (\w+)$/);
  return `for ${every ? `${every[1]} ${every[2]}` : `1 ${cadence}`}`;
};

export const formatOptionCadence = (plan: PaymentPlanOption): string => {
  const cadence = plan.cadence || 'Monthly';
  if (cadence === 'Lifetime') return 'Lifetime';
  const base: Record<string, [string, number]> = {
    Daily: ['DAY', 1], Weekly: ['WEEK', 1], Monthly: ['MONTH', 1],
    Quarterly: ['MONTH', 3], 'Half-Yearly': ['MONTH', 6], Yearly: ['YEAR', 1],
  };
  const [interval, fixed] = base[cadence] || base.Monthly;
  const custom = Math.floor(Number(plan.intervalCount));
  const overridable = ['Daily', 'Weekly', 'Monthly', 'Yearly'].includes(cadence);
  return formatPlanCadence({
    interval,
    intervals: overridable && custom > 0 ? custom : fixed,
  } as GatewayPlan);
};

export const formatPlanMoney = (value: number, currency?: string): string => {
  const symbol = !currency || currency.toUpperCase() === 'INR' ? '₹' : `${currency} `;
  return `${symbol}${value.toLocaleString('en-IN')}`;
};

/** "₹499 / Month", or "" when the gateway gave no amount. */
export const formatPlanPrice = (plan: GatewayPlan): string =>
  plan.amount === undefined
    ? ''
    : `${formatPlanMoney(plan.amount, plan.currency)} / ${formatPlanCadence(plan)}`;

export const paymentApi = {
  /** The server rebuilds amount, plan and order fields from the saved form. */
  createFormOrder: async (tenantId: string, formId: string, payload: CreateFormOrderPayload) => {
    const response = await axios.post(
      `${API_BASE_URL}/tenant/${tenantId}/form/${formId}/payment/create-order`,
      payload,
    );
    return response.data;
  },

  getPaymentRecord: async (
    tenantId: string,
    paymentId: string,
    provider: string = 'cashfree',
    live = false,
  ) => {
    const response = await axios.get(
      `${gatewayBase(provider)}/api/tenant/${tenantId}/customer/get-payment-record/${paymentId}`,
      live ? { params: { isLive: true } } : undefined,
    );
    return response.data;
  },

  getPlans: async (
    tenantId: string,
    provider: string = 'cashfree',
  ): Promise<GatewayPlan[]> => {
    const response = await axios.get(
      `${gatewayBase(provider)}/api/tenant/${tenantId}/customer/get-plans`,
    );
    return planRows(response.data)
      .map(normalizePlan)
      .filter((plan) => plan.id);
  },
};
